import { useCallback, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  sendAnalyticsEvent,
  sendAnalyticsEventBeacon,
  sendPublicAnalyticsEvent,
  sendPublicAnalyticsEventBeacon,
  type AnalyticsAttributes,
} from './analytics';
import { getFaroSessionId } from './faro';
import {
  trackPostHogPageleave,
  trackPostHogPageview,
} from './posthog';

type PageAnalyticsOptions = {
  enabled?: boolean;
  screenName?: string;
};

function buildScreenAttributes(
  screenName: string,
  route: string,
  pathname: string,
  search: string,
  userId?: string,
  username?: string,
): AnalyticsAttributes {
  const attributes: AnalyticsAttributes = {
    screen_name: screenName,
    route,
    path: pathname,
    query: search || '',
    session_id: getFaroSessionId(),
    source: 'react_router',
  };
  if (userId) attributes.user_id = userId;
  if (username) attributes.user_username = username;
  return attributes;
}

export function usePageAnalytics(options: PageAnalyticsOptions = {}) {
  const { enabled = true } = options;
  const location = useLocation();
  const { user } = useAuth();
  const enteredAtRef = useRef<number | null>(null);
  const activeScreenRef = useRef<{ screenName: string; route: string } | null>(
    null,
  );

  const routeKey = `${location.pathname}${location.search}`;
  const screenName = options.screenName ?? location.pathname;
  const isAuthenticated = Boolean(user?.id);

  const emitViewScreen = useCallback(async () => {
    const attributes = buildScreenAttributes(
      screenName,
      routeKey,
      location.pathname,
      location.search,
      user?.id,
      user?.username,
    );
    if (isAuthenticated) {
      await sendAnalyticsEvent('view_screen', attributes);
    } else {
      await sendPublicAnalyticsEvent('view_screen', attributes);
    }
    trackPostHogPageview(screenName, {
      route: routeKey,
      path: location.pathname,
    });
  }, [
    isAuthenticated,
    location.pathname,
    location.search,
    routeKey,
    screenName,
    user?.id,
    user?.username,
  ]);

  const emitLeaveScreen = useCallback(
    (useBeacon: boolean) => {
      const active = activeScreenRef.current;
      const enteredAt = enteredAtRef.current;
      if (!active || enteredAt === null) return;

      const durationMs = Math.max(0, Date.now() - enteredAt);
      const attributes = buildScreenAttributes(
        active.screenName,
        active.route,
        location.pathname,
        location.search,
        user?.id,
        user?.username,
      );
      attributes.duration_ms = durationMs;

      if (useBeacon) {
        if (isAuthenticated) {
          sendAnalyticsEventBeacon('leave_screen', attributes);
        } else {
          sendPublicAnalyticsEventBeacon('leave_screen', attributes);
        }
        return;
      }

      const sendLeave = isAuthenticated
        ? sendAnalyticsEvent('leave_screen', attributes)
        : sendPublicAnalyticsEvent('leave_screen', attributes);
      void sendLeave.catch(() => {});
      trackPostHogPageleave(active.screenName, durationMs, {
        route: active.route,
      });
    },
    [
      isAuthenticated,
      location.pathname,
      location.search,
      user?.id,
      user?.username,
    ],
  );

  useEffect(() => {
    if (!enabled) return;

    if (
      activeScreenRef.current &&
      activeScreenRef.current.route !== routeKey
    ) {
      emitLeaveScreen(false);
    }

    activeScreenRef.current = { screenName, route: routeKey };
    enteredAtRef.current = Date.now();
    void emitViewScreen().catch(() => {});

    return () => {
      emitLeaveScreen(false);
      activeScreenRef.current = null;
      enteredAtRef.current = null;
    };
  }, [enabled, emitLeaveScreen, emitViewScreen, routeKey, screenName]);

  useEffect(() => {
    if (!enabled) return;

    const onHidden = () => {
      if (document.visibilityState === 'hidden') {
        emitLeaveScreen(true);
      }
    };
    const onBeforeUnload = () => {
      emitLeaveScreen(true);
    };

    document.addEventListener('visibilitychange', onHidden);
    window.addEventListener('pagehide', onBeforeUnload);
    return () => {
      document.removeEventListener('visibilitychange', onHidden);
      window.removeEventListener('pagehide', onBeforeUnload);
    };
  }, [enabled, emitLeaveScreen]);
}
